/**
 * EscaladeScreen — the D-29 critical-value escalation view. NOVA-051: the work-set is now the DURABLE
 * server worklist (GET /validation/escalades?statut=a_acquitter, `useEscaladeWorklist`) — survives a
 * reload/relog, unlike the old in-session opened-list (kept only as a same-session convenience). A manual
 * id lookup (deep-linkable via ?id=) is retained. Selecting one opens the EscaladeDetail drawer where the
 * read-back ack closes the loop, ladder attempts are logged, and break-glass is triggered.
 *
 * All actions inside the drawer are gated on `validation.valeurcritique.acknowledge`. Reaching the
 * screen requires the module read cap (ProtectedRoute). 5 states: the durable worklist drives loading /
 * error / empty; offline + forbidden are the remaining live states; per-action states live in the drawer.
 */
import { useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { Badge, Button, Card, EmptyState, ErrorState, ForbiddenState, FormField, Input, OfflineState, Spinner } from "@/ui";
import { useT, useUiStore } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { CAP, type Id } from "../types";
import { useEscaladeWorklist, useOpenedEscalades } from "../hooks/useValidation";
import { EscaladeDetail } from "../components/EscaladeDetail";

interface ActiveEscalade {
  escaladeId: Id;
  resultatId?: Id | null;
}

export function EscaladeScreen() {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const canAck = useCan(CAP.acknowledge);
  const refs = useOpenedEscalades((s) => s.refs);
  // NOVA-051 — the DURABLE worklist (a_acquitter), gated on the same ack cap as its actions.
  const worklist = useEscaladeWorklist("a_acquitter");
  const worklistItems = worklist.data?.escalades ?? [];

  const [searchParams, setSearchParams] = useSearchParams();
  const [lookup, setLookup] = useState("");
  const [active, setActive] = useState<ActiveEscalade | null>(() => {
    const id = searchParams.get("id");
    return id ? { escaladeId: id } : null;
  });

  const sortedRefs = useMemo(() => [...refs].sort((a, b) => b.openedAt - a.openedAt), [refs]);

  const openDetail = (esc: ActiveEscalade) => {
    setActive(esc);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("id", String(esc.escaladeId));
      return next;
    });
  };
  const closeDetail = () => {
    setActive(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("id");
      return next;
    });
  };

  const submitLookup = (e: FormEvent) => {
    e.preventDefault();
    const id = lookup.trim();
    if (!id) return;
    openDetail({ escaladeId: id });
    setLookup("");
  };

  if (!online) return <OfflineState cached={false} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
      <header style={{ display: "flex", flexDirection: "column", gap: token.s1 }}>
        <h1 style={{ fontSize: token.fsH1, margin: 0 }}>{t("validation.escalade.title")}</h1>
        <span style={{ color: token.slate, fontSize: token.fsLabel }}>{t("validation.escalade.subtitle")}</span>
      </header>

      {!canAck && (
        <ForbiddenState title={t("validation.escalade.title")} requiredCapabilities={[CAP.acknowledge]} />
      )}

      <Card title={t("validation.escalade.lookup")}>
        <form onSubmit={submitLookup} style={{ display: "flex", gap: token.s3, alignItems: "flex-end", flexWrap: "wrap" }}>
          <FormField
            label={t("validation.escalade.lookup")}
            hint={t("validation.escalade.lookup.hint")}
            className="nv-validation-grow"
          >
            {(p) => (
              <Input
                {...p}
                mono
                value={lookup}
                onChange={(e) => setLookup(e.target.value)}
                placeholder={t("validation.id.placeholder")}
                inputMode="numeric"
              />
            )}
          </FormField>
          <Button type="submit" disabled={lookup.trim().length === 0}>
            {t("validation.escalade.open")}
          </Button>
        </form>
      </Card>

      {/* NOVA-051 — the DURABLE escalation worklist (a_acquitter), the authoritative work-set. */}
      <Card
        title={t("validation.escalade.worklist")}
        padded={worklist.isLoading || worklist.isError || worklistItems.length === 0}
      >
        {worklist.isLoading ? (
          <Spinner label={t("validation.escalade.worklist.loading")} />
        ) : worklist.isError ? (
          <ErrorState
            message={t("validation.escalade.worklist.error")}
            code={worklist.error?.code}
            onRetry={() => void worklist.refetch()}
          />
        ) : worklistItems.length === 0 ? (
          <EmptyState glyph="◆" title={t("validation.escalade.worklist.empty")} />
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
            {worklistItems.map((e) => (
              <li
                key={String(e.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: token.s3,
                  padding: token.s3,
                  borderBlockEnd: `1px solid ${token.lineFaint}`,
                }}
              >
                <Badge tone="critical">{t(`validation.escalade.statut.${e.statut}`)}</Badge>
                {e.resultat_id != null && (
                  <span className="nv-mono nv-ltr-island">
                    {t("validation.escalade.fromResult", { id: String(e.resultat_id) })}
                  </span>
                )}
                <span className="nv-mono nv-ltr-island" style={{ color: token.steel, marginInlineStart: token.s2 }}>
                  #{String(e.id)}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  style={{ marginInlineStart: "auto" }}
                  onClick={() => openDetail({ escaladeId: e.id, resultatId: e.resultat_id })}
                >
                  {t("validation.escalade.open")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title={t("validation.escalade.recent")} padded={sortedRefs.length === 0}>
        {sortedRefs.length === 0 ? (
          <EmptyState glyph="◆" title={t("validation.escalade.recent.empty")} />
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
            {sortedRefs.map((r) => (
              <li
                key={String(r.escaladeId)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: token.s3,
                  padding: token.s3,
                  borderBlockEnd: `1px solid ${token.lineFaint}`,
                }}
              >
                <Badge tone="critical">{t("validation.escalade.statut.a_acquitter")}</Badge>
                <span className="nv-mono nv-ltr-island">
                  {t("validation.escalade.fromResult", { id: String(r.resultatId) })}
                </span>
                <span className="nv-mono nv-ltr-island" style={{ color: token.steel, marginInlineStart: token.s2 }}>
                  #{String(r.escaladeId)}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  style={{ marginInlineStart: "auto" }}
                  onClick={() => openDetail({ escaladeId: r.escaladeId, resultatId: r.resultatId })}
                >
                  {t("validation.escalade.open")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {active && (
        <EscaladeDetail
          open={active !== null}
          onClose={closeDetail}
          escaladeId={active.escaladeId}
          resultatId={active.resultatId ?? null}
        />
      )}
    </div>
  );
}
